import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, MapPin, Phone, Calendar, Shield, FileText } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';

const PriorityDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [records, setRecords] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // API_URL is now managed in src/config.js
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // 1. Fetch Basic Patient Info
        const patientRes = await axios.get(`${API_URL}?action=get_patient_details&id=${id}`);
        if (patientRes.data && patientRes.data.patient) {
             const p = patientRes.data.patient;
             setPatientInfo({
                full_name: p.full_name,
                phone_number: p.phone_number,
                address: p.address,
                dob: p.dob,
                sex: p.sex,
                insurance: p.insurance_provider,
                status: p.status
            });
        }

        // 2. Fetch Detailed Prescription History
        const historyRes = await axios.get(`${API_URL}?action=get_priority_details&id=${id}`);
        if (Array.isArray(historyRes.data)) {
            setRecords(historyRes.data);
            // If history exists, overwrite specific details if needed, but step 1 ensures header is always full
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error loading details:", error);
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading Record...</div>;
  if (!patientInfo) return <div className="p-10 text-center text-red-500">Patient not found.</div>;

  return (
    <div className="space-y-6">
      
      {/* 1. HEADER & BACK BUTTON */}
      <div className="flex items-center justify-between">
        {/* ✅ Theme Change: Hover Teal */}
        <button 
            onClick={() => navigate('/priority')} 
            className="flex items-center text-gray-600 hover:text-teal-600 transition font-bold"
        >
            <ArrowLeft size={20} className="mr-2"/> Back to Priority List
        </button>
        <h2 className="text-2xl font-bold text-gray-800">Patient Medical Record</h2>
      </div>

      {/* 2. PATIENT INFO FORM */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        {/* ✅ Theme Change: Text Teal */}
        <h3 className="text-lg font-bold text-teal-700 mb-4 flex items-center gap-2">
            <User size={20}/> Patient Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Name & Sex */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Patient Name</label>
                <p className="text-gray-800 font-bold text-lg">{patientInfo.full_name}</p>
                {/* ✅ Theme Change: Teal Badge */}
                <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-0.5 rounded-full">
                    {patientInfo.sex || 'N/A'}
                </span>
            </div>

            {/* Contact */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Contact Details</label>
                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                    <Phone size={14} className="text-gray-400"/> {patientInfo.phone_number || 'N/A'}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin size={14} className="text-gray-400"/> 
                    <span className="truncate" title={patientInfo.address}>{patientInfo.address || 'N/A'}</span>
                </div>
            </div>

            {/* DOB & Insurance */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Personal Data</label>
                <div className="flex items-center gap-2 text-sm text-gray-700 mb-1">
                    <Calendar size={14} className="text-gray-400"/> DOB: {formatDate(patientInfo.dob)}
                </div>
                <div className="flex items-center gap-2 text-sm text-green-700 font-bold">
                    <Shield size={14}/> {patientInfo.insurance || 'No Insurance'}
                </div>
            </div>

            {/* Status */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex flex-col justify-center items-center">
                <label className="text-xs font-bold text-gray-400 uppercase block mb-1">Account Status</label>
                <span className={`px-4 py-1 rounded-full text-sm font-bold ${
                  ['F', 'B', 'U'].includes(patientInfo.status) ? 'bg-teal-100 text-teal-700' :
                  patientInfo.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                    {patientInfo.status === 'F' ? 'Follow Up (F)' :
                     patientInfo.status === 'B' ? 'Billing (B)' :
                     patientInfo.status === 'U' ? 'Urgent (U)' :
                     (patientInfo.status || 'Active')}
                </span>
            </div>
        </div>
      </div>

      {/* 3. MASSIVE DATA TABLE */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200">
        <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
            <FileText size={18} className="text-gray-500"/>
            <h3 className="font-bold text-gray-700">Prescription History Details</h3>
        </div>

        <div className="overflow-x-auto">
            <table className="min-w-max text-xs text-left text-gray-600">
                <thead className="bg-gray-100 text-gray-700 uppercase font-bold">
                    <tr>
                        <th className="px-4 py-3 border-b">Rx #</th>
                        <th className="px-4 py-3 border-b">Ref/Auth #</th>
                        <th className="px-4 py-3 border-b">Filled Date</th>
                        <th className="px-4 py-3 border-b">Ordered Date</th>
                        <th className="px-4 py-3 border-b">Sex</th>
                        <th className="px-4 py-3 border-b">Drug Name</th>
                        <th className="px-4 py-3 border-b">NDC</th>
                        <th className="px-4 py-3 border-b text-center">Qty</th>
                        <th className="px-4 py-3 border-b text-center">Days</th>
                        <th className="px-4 py-3 border-b text-right">Amount</th>
                        <th className="px-4 py-3 border-b text-right">Copay</th>
                        <th className="px-4 py-3 border-b">Type</th>
                        <th className="px-4 py-3 border-b">Pt Insurance</th>
                        <th className="px-4 py-3 border-b">Brand</th>
                        <th className="px-4 py-3 border-b">Class</th>
                        <th className="px-4 py-3 border-b">DAW</th>
                        <th className="px-4 py-3 border-b">RX Origin</th>
                        <th className="px-4 py-3 border-b">Prescriber</th>
                        <th className="px-4 py-3 border-b">NPI</th>
                        <th className="px-4 py-3 border-b">License #</th>
                        <th className="px-4 py-3 border-b text-center">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {records.length === 0 ? (
                         <tr><td colSpan="21" className="p-6 text-center text-gray-400 italic">No prescription history found.</td></tr>
                    ) : (
                        records.map((item, i) => (
                        <tr key={i} className="hover:bg-teal-50 transition">
                            {/* ✅ Theme Change: Teal Text for RX */}
                            <td className="px-4 py-3 font-medium text-teal-600">{item.rx_no}</td>
                            <td className="px-4 py-3">{item.auth_ref_number}</td>
                            <td className="px-4 py-3">{formatDate(item.last_fill_date)}</td>
                            <td className="px-4 py-3">{formatDate(item.order_date)}</td>
                            <td className="px-4 py-3">{item.sex}</td>
                            <td className="px-4 py-3 font-bold text-gray-800">{item.drug_name}</td>
                            <td className="px-4 py-3 font-mono">{item.ndc}</td>
                            <td className="px-4 py-3 text-center">{item.quantity}</td>
                            <td className="px-4 py-3 text-center">{item.days_supply}</td>
                            <td className="px-4 py-3 text-right">${item.amount}</td>
                            <td className="px-4 py-3 text-right text-red-600 font-bold">${item.copay}</td>
                            <td className="px-4 py-3">{item.drug_type}</td>
                            <td className="px-4 py-3">{item.insurance_display}</td>
                            <td className="px-4 py-3">{item.brand}</td>
                            <td className="px-4 py-3">{item.drug_class}</td>
                            <td className="px-4 py-3">{item.daw}</td>
                            <td className="px-4 py-3">{item.rx_origin}</td>
                            <td className="px-4 py-3 text-gray-900">{item.prescriber_name}</td>
                            <td className="px-4 py-3 font-mono">{item.prescriber_npi}</td>
                            <td className="px-4 py-3 font-mono">{item.prescriber_license}</td>
                            <td className="px-4 py-3 text-center">
                                {/* ✅ Theme Change: Teal/Green Badges */}
                                <span className={`px-2 py-1 rounded text-[10px] font-bold ${item.status === 'Picked' ? 'bg-green-100 text-green-800' : 'bg-teal-100 text-teal-800'}`}>
                                    {item.status}
                                </span>
                            </td>
                        </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
};

export default PriorityDetails;